import { GRIPP_WORKSPACE, RoutineConfig, RoutineType, useRoles } from '@gripp/shared-logic';
import {
  textStyles,
  Colors,
  CommonLabel,
  ControlledCheckboxItem,
  FormContext,
  PermittedRolesSelector,
  RoutineAdminInputText,
  RoutineModules,
  SaveFormBottomOld,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { array, boolean, string, object as yupObject } from 'yup';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
type RoutineAdminFormProps = {
  type: RoutineType;
  config: RoutineConfig;
  saveButtonText: string;
  onSubmit: (data: any) => Promise<void>;
  workspaceId?: string;
  isWorkspaceRoutine?: boolean;
};

export const RoutineEditForm = (props: RoutineAdminFormProps) => {
  const i18nPrefix = 'routines.admin';
  const module = RoutineModules[props.type];
  const AdminForm = module.adminForm ? module.adminForm : () => <></>;
  const { roles } = useRoles();
  const { t } = useTranslation();
  const isGrippWorkspace = props.workspaceId === GRIPP_WORKSPACE;

  const schemaObject = {
    title: string().required(),
    routineButtonText: string().required(),
    issuePrefix: string().required(),
    reportOnException: boolean().optional(),
    oneTimeOnly: boolean().optional(),
    autoPrompt: boolean().optional(),
    idpEnabled: boolean().optional(),
    idpDocumentDescription: string().optional(),
    permittedRoles: array().of(string()).nullable(),
    ...module.adminSchema,
  };

  if (props.type !== RoutineType.Workflow) {
    schemaObject.instructions = string().required();
  }

  const formSchema = yupObject(schemaObject);

  const formDefaultValues = useMemo(() => {
    const defaultValues: any = {
      ...props.config,
    };
    if (props.type !== RoutineType.Workflow) {
      defaultValues.instructions = defaultValues.instructions ?? '';
    }
    defaultValues.oneTimeOnly = defaultValues.oneTimeOnly ?? false;
    defaultValues.autoPrompt = defaultValues.autoPrompt ?? false;
    defaultValues.idpEnabled = defaultValues.idpEnabled ?? false;
    defaultValues.idpDocumentDescription =
      defaultValues.idpDocumentDescription ?? '';
    defaultValues.autoLabels = defaultValues.autoLabels ?? [];
    defaultValues.permittedRoles = defaultValues.permittedRoles ?? null;
    defaultValues.isWorkspaceRoutine = props.isWorkspaceRoutine ?? false;

    return defaultValues;
  }, [props.type, props.config]);

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: formDefaultValues,
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  // The descriptor is only meaningful once IDP is on, and only Workflow routines
  // have the toggle at all — watched rather than read once so unchecking the box
  // collapses the field immediately.
  const idpEnabled = !!useWatch({ control, name: 'idpEnabled' });
  const showIdpDocumentDescription =
    props.type === RoutineType.Workflow && idpEnabled;

  const onSubmit = async (data: any) => {
    await props.onSubmit(data).then(() => {
      reset(data);
    });
  };

  return (
    <FormContext.Provider value={formMethods}>
      <ScrollView>
        <View>
          <SaveFormBottomOld
            onSubmit={handleSubmit(onSubmit)}
            saveLabel={props.saveButtonText}
            isValid={isValid}
            isDirty={isDirty}
            buttonStyle={styles.saveButton}
          >
            <View style={styles.typeRow}>
              <View style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
                <View style={{ flexDirection: 'row' }}>
                  <Text style={textStyles.default}>{t('routines.admin.type')}: </Text>
                  <Text style={styles.type}>
                    {t(`routines.types.${props.type}`)}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Controller
                    control={control}
                    name="permittedRoles"
                    render={({ field: { onChange, value } }) => (
                      <PermittedRolesSelector
                        roles={roles}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <ControlledCheckboxItem
                    control={control}
                    name="autoPinning"
                    label={t('routines.admin.enableAutoPinning')}
                    position="trailing"
                    style={{ marginRight: 0, paddingRight: 0 }}
                  />
                  <ControlledCheckboxItem
                    control={control}
                    name="oneTimeOnly"
                    label={t('routines.admin.oneTimeOnly')}
                    position="trailing"
                    style={{ marginRight: 0, paddingRight: 0 }}
                  />
                  <ControlledCheckboxItem
                    control={control}
                    name="autoPrompt"
                    label={t('routines.admin.autoPrompt')}
                    position="trailing"
                    style={{ marginRight: 0, paddingRight: 0 }}
                  />
                  {props.type === RoutineType.Workflow && (
                    <ControlledCheckboxItem
                      control={control}
                      name="idpEnabled"
                      label={t('routines.admin.idpEnabled')}
                      position="trailing"
                      style={{ marginRight: 0, paddingRight: 0 }}
                    />
                  )}
                  {isGrippWorkspace && (
                    <ControlledCheckboxItem
                      control={control}
                      name="isWorkspaceRoutine"
                      label={t('routines.admin.isWorkspaceRoutine')}
                      position="trailing"
                      style={{ marginRight: 0, paddingRight: 0 }}
                    />
                  )}
                </View>
                {(props.type === RoutineType.Workflow ||
                  props.type === RoutineType.Status) && (
                  <View>
                    <ControlledCheckboxItem
                      control={control}
                      name="reportOnException"
                      label={t('routines.admin.reportOnException')}
                      position="trailing"
                      style={{ marginRight: 0, paddingRight: 0 }}
                    />
                  </View>
                )}
              </View>
            </View>
            {props.workspaceId && (
              <View style={styles.labelSection}>
                <CommonLabel
                  workspaceId={props.workspaceId}
                  fieldName="autoLabels"
                  title={t('routines.admin.autoAddLabel')}
                  backgroundColor={Colors.role.surfaceHover}
                />
              </View>
            )}
            <RoutineAdminInputText
              control={control}
              name="title"
              i18nPrefix={i18nPrefix}
            />
            <RoutineAdminInputText
              control={control}
              name="routineButtonText"
              i18nPrefix={i18nPrefix}
            />
            <RoutineAdminInputText
              control={control}
              name="issuePrefix"
              i18nPrefix={i18nPrefix}
            />
            {props.type !== RoutineType.Workflow && (
              <RoutineAdminInputText
                control={control}
                name="instructions"
                i18nPrefix={i18nPrefix}
                textArea={true}
              />
            )}
            {showIdpDocumentDescription && (
              <>
                <RoutineAdminInputText
                  control={control}
                  name="idpDocumentDescription"
                  i18nPrefix={i18nPrefix}
                  textArea={true}
                />
                <Text style={styles.idpHint}>
                  {t('routines.admin.idpDocumentDescriptionHint')}
                </Text>
              </>
            )}
            <AdminForm
              control={control}
              config={props.config}
              mediaPicker={MediaPicker}
            />
          </SaveFormBottomOld>
        </View>
      </ScrollView>
    </FormContext.Provider>
  );
};

const styles = StyleSheet.create({
  typeRow: {
    flexDirection: 'row',
    marginBottom: 10,
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
  type: {
    fontWeight: '700',
    color: Colors.role.textStrong,
  },
  saveButton: {
    alignSelf: 'flex-end',
    marginTop: 20,
  },
  labelSection: {
    marginBottom: 20,
  },
  // Pulled up under RoutineAdminInputText's own 20pt bottom margin, matching the
  // idpPromptHint treatment on the Text question admin form.
  idpHint: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.role.textSecondary,
    marginTop: -12,
    marginBottom: 20,
  },
});
